import React, { useRef, useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import { ReactComponent as Carot } from '@jetshop/ui/svg/Carrot.svg';
import { theme } from '../Theming/Theming';
import { Above } from '@jetshop/ui/Breakpoints';

const SliderBoxWrapper = styled('div')`
  position: relative;
  .slider-track {
    overflow-x: auto;
    /* width */
    ::-webkit-scrollbar {
      height: 0px;
    }
  }
  &.end-space {
    .slider-track {
      padding: 0 1rem;
      .slider-items {
        &:after {
          flex-shrink: 0;
          content: '';
          width: 1px;
        }
      }
    }
  }
  &.scroll-bar {
    .slider-track {
      padding-bottom: 15px;
      /* width */
      ::-webkit-scrollbar {
        height: 5px;
      }
      /* Track */
      ::-webkit-scrollbar-track {
        background: ${theme.colors.primaryLight};
      }
      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: ${theme.colors.primary};
      }

      /* Handle on hover */
      ::-webkit-scrollbar-thumb:hover {
        background: ${theme.colors.primary};
      }
    }
  }
  .slider-items {
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    > * {
      flex-shrink: 0;
    }
  }
  &.gradient {
    &.prev:before {
      background: linear-gradient(
        to right,
        white 0%,
        rgba(255, 255, 255, 0) 100%
      );
      content: '';
      height: 100%;
      position: absolute;
      width: 5px;
      z-index: 2;
      left: 0px;
      top: 0px;
    }
    &.next:after {
      background: linear-gradient(
        to left,
        white 0%,
        rgba(255, 255, 255, 0) 100%
      );
      content: '';
      height: 100%;
      position: absolute;
      width: 5px;
      z-index: 2;
      right: 0px;
      top: 0px;
    }
  }
`;

const ButtonWrapper = styled('button')`
  position: absolute;
  height: 50px;
  width: 25px;
  &.standard-nav {
    top: calc(50% - 25px);
  }
  &.bottom-nav {
    bottom: 0;
  }
  svg {
    font-size: 9px;
  }
  ${theme.above.xl} {
    svg {
      font-size: 11px;
    }
  }
  box-shadow: 0px 0px 9px #00000026;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0.9;
  &:hover {
    opacity: 1;
  }

  &.next {
    border-bottom-left-radius: 50px;
    border-top-left-radius: 50px;
    right: 0;
    justify-content: flex-end;
    svg {
      transform: rotate(-90deg);
    }
  }
  &.prev {
    border-bottom-right-radius: 50px;
    border-top-right-radius: 50px;
    left: 0;
    justify-content: flex-start;
    svg {
      transform: rotate(90deg);
    }
  }
`;

const LoadingProductWrapper = styled.div`
  margin: 2rem auto 0;
  text-align: center;
  .loadingbar {
    margin: 0.5rem auto 0;
    position: relative;
    width: 16rem;
    background: ${theme.colors.primaryLight};
    height: 5px;
  }
`;

const Progress = styled.div`
  background: ${theme.colors.primary};
  position: absolute;
  left: 0;
  top: 0;
  height: 5px;
`;

const SliderButton = ({ dir, className, ...rest }) => (
  <ButtonWrapper
    className={`${dir === 'next' ? 'next' : 'prev'} ${className}`}
    {...rest}
  >
    <Carot />
  </ButtonWrapper>
);

const ProgressBar = ({ width, left, offsetWidth }) => {
  if (width === 0) return null;
  return (
    <LoadingProductWrapper percent={left / width}>
      <div className="loadingbar">
        <Progress
          className="progress"
          style={{
            width: (offsetWidth / width) * 100 + '%',
            left: (left / width) * 100 + '%',
          }}
        />
      </div>
    </LoadingProductWrapper>
  );
};

const NavButtons = ({
  showNavInMobile,
  scrollContainerBy,
  next,
  prev,
  className = '',
}) => (
  <Above breakpoint="lg">
    {(matches) => {
      if (matches || showNavInMobile)
        return (
          <div className="nav-buttons">
            {prev && (
              <SliderButton
                className={`${className}`}
                dir={'prev'}
                onClick={() => {
                  scrollContainerBy(true);
                }}
              />
            )}

            {next && (
              <SliderButton
                className={`${className}`}
                dir={'next'}
                onClick={() => {
                  scrollContainerBy();
                }}
              />
            )}
          </div>
        );
      return null;
    }}
  </Above>
);

export const ScrollBox = ({
  children,
  gradient = false,
  showNavInMobile = false,
  bottomNav = false,
  scrollBar = false,
  progressBar = false,
  className,
  focusElement = false,
  endSpace = true,
}) => {
  const sliderRef = useRef(null);
  const [next, setNext] = useState(false);
  const [prev, setPrev] = useState(false);
  const [left, setLeft] = useState(0);
  const [init, setInit] = useState(1);

  function scrollContainerBy(back = false) {
    const width = sliderRef.current.offsetWidth / 2;
    const distance = back ? -width : width;
    sliderRef.current.scrollBy({ left: distance, behavior: 'smooth' });
  }

  useEffect(() => {
    //RUN ON SECOND RENDER
    if (focusElement && init < 3) {
      const focus = document.querySelectorAll(focusElement);
      if (focus.length) {
        sliderRef.current.scrollTo({
          left: focus[0].offsetLeft - sliderRef.current.offsetWidth / 2,
        });
        setInit(init + 1);
      }
    }

    const handleScroll = (event) => {
      if (!prev && event.target.scrollLeft > 0) setPrev(true);

      if (prev && event.target.scrollLeft === 0) setPrev(false);

      if (
        next &&
        event.target.scrollLeft + event.target.offsetWidth ===
          event.target.scrollWidth
      )
        setNext(false);
      if (
        !next &&
        event.target.scrollLeft + event.target.offsetWidth <
          event.target.scrollWidth
      )
        setNext(true);

      setLeft(event.target?.scrollLeft);
    };

    const element = sliderRef.current;

    //INITIAL
    if (!prev && element.scrollWidth > element.offsetWidth) {
      setNext(true);
    }

    element.addEventListener('scroll', handleScroll);
    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [prev, next, focusElement, init]);

  let cssClass = gradient ? 'gradient' : '';
  cssClass += prev ? ' prev' : '';
  cssClass += next ? ' next' : '';
  cssClass += scrollBar ? ' scroll-bar' : '';
  cssClass += !prev && !next ? ' no-scroll' : '';
  cssClass += endSpace ? ' end-space' : '';

  const navProps = {
    ...{
      scrollContainerBy,
      showNavInMobile,
      next,
      prev,
    },
  };

  return (
    <SliderBoxWrapper className={`slider-box-wrapper ${cssClass} ${className}`}>
      <NavButtons {...navProps} className="standard-nav" />
      <div className="slider-track" ref={sliderRef}>
        <div className="slider-items">{children}</div>
      </div>
      {bottomNav && <NavButtons {...navProps} className="bottom-nav" />}
      {progressBar && (
        <ProgressBar
          width={sliderRef.current?.scrollWidth}
          offsetWidth={sliderRef.current?.offsetWidth}
          left={left}
        />
      )}
    </SliderBoxWrapper>
  );
};

/*
USAGE
<ScrollSlider scrollDistance={600} direction="vertical">
  {products.map((product, index) => (
    <ProductCard
      product={product}
      key={product.id}
    />
  ))}
</ScrollSlider>
*/
